import { useState, useCallback, useEffect, useRef } from 'react';

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 20000;

/**
 * Hook para gerenciar toast notifications com suporte a:
 * - Fila de pendentes quando aba está oculta (Page Visibility API)
 * - Auto-dismiss após 20 segundos (pausável no hover)
 * - Máximo de 3 toasts visíveis simultaneamente
 */
export function useNotificationToast() {
  const [activeToasts, setActiveToasts] = useState([]);
  const pendingQueueRef = useRef([]);
  const overflowQueueRef = useRef([]);
  const timersRef = useRef({});

  // Promove toasts da fila de overflow para ativos quando há espaço
  const promoteFromOverflow = useCallback(() => {
    setActiveToasts((prev) => {
      const available = MAX_VISIBLE - prev.length;
      if (available <= 0 || overflowQueueRef.current.length === 0) return prev;

      const toPromote = overflowQueueRef.current.splice(0, available);
      return [...prev, ...toPromote];
    });
  }, []);

  // Inicia timer de auto-dismiss para um toast
  const startTimer = useCallback((toastId) => {
    if (timersRef.current[toastId]) return;
    timersRef.current[toastId] = setTimeout(() => {
      delete timersRef.current[toastId];
      setActiveToasts((prev) => prev.filter((t) => t._toastId !== toastId));
    }, AUTO_DISMISS_MS);
  }, []);

  // Pausa timer (hover)
  const pauseTimer = useCallback((toastId) => {
    if (timersRef.current[toastId]) {
      clearTimeout(timersRef.current[toastId]);
      delete timersRef.current[toastId];
    }
  }, []);

  // Retoma timer (mouse leave)
  const resumeTimer = useCallback((toastId) => {
    startTimer(toastId);
  }, [startTimer]);

  // Dismiss manual
  const dismiss = useCallback((toastId) => {
    if (timersRef.current[toastId]) {
      clearTimeout(timersRef.current[toastId]);
      delete timersRef.current[toastId];
    }
    setActiveToasts((prev) => prev.filter((t) => t._toastId !== toastId));
  }, []);

  // Enfileira uma notificação
  const enqueue = useCallback((notification) => {
    const toast = {
      ...notification,
      _toastId: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    };

    if (document.hidden) {
      pendingQueueRef.current.push(toast);
      return;
    }

    setActiveToasts((prev) => {
      if (prev.length >= MAX_VISIBLE) {
        overflowQueueRef.current.push(toast);
        return prev;
      }
      return [...prev, toast];
    });
  }, []);

  // Listener de visibilidade: ao voltar foco, exibe pendentes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;

      const pending = pendingQueueRef.current.splice(0);
      if (pending.length === 0) return;

      setActiveToasts((prev) => {
        const available = MAX_VISIBLE - prev.length;
        const toShow = pending.slice(0, Math.max(available, 0));
        const toOverflow = pending.slice(Math.max(available, 0));
        overflowQueueRef.current.push(...toOverflow);
        return [...prev, ...toShow];
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Inicia timers para toasts novos e promove overflow quando há espaço
  useEffect(() => {
    activeToasts.forEach((t) => startTimer(t._toastId));
    if (activeToasts.length < MAX_VISIBLE) {
      promoteFromOverflow();
    }
  }, [activeToasts, startTimer, promoteFromOverflow]);

  // Cleanup timers ao desmontar
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  return { activeToasts, enqueue, dismiss, pauseTimer, resumeTimer };
}

export default useNotificationToast;
